import { useEffect } from 'react';
import { useLockStore } from '@/stores/lockStore';
import { useAuthStore } from '@/stores/authStore';

const EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * Auto-locks the app after `idleTimeoutMin` minutes of no user input.
 * No-op when the user isn't authenticated, biometric isn't enrolled, or
 * the timeout is 0 (off). Mount once at App root.
 */
export function useIdleLock() {
  const isAuthenticated = useAuthStore((s) => s.status === 'authenticated');
  const credentialId = useLockStore((s) => s.credentialId);
  const idleTimeoutMin = useLockStore((s) => s.idleTimeoutMin);
  const isLocked = useLockStore((s) => s.isLocked);
  const lock = useLockStore((s) => s.lock);

  useEffect(() => {
    if (!isAuthenticated || !credentialId || !idleTimeoutMin || isLocked) return;

    const timeoutMs = idleTimeoutMin * 60_000;
    let timer = window.setTimeout(lock, timeoutMs);
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(lock, timeoutMs);
    };

    EVENTS.forEach((evt) => window.addEventListener(evt, reset, { passive: true }));
    return () => {
      window.clearTimeout(timer);
      EVENTS.forEach((evt) => window.removeEventListener(evt, reset));
    };
  }, [isAuthenticated, credentialId, idleTimeoutMin, isLocked, lock]);
}
